import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, createToaster, Icon, Input, NativeSelect, Stack, Text, Toast, Toaster, Dialog, Portal } from '@chakra-ui/react';
import { createContext, useCallback, useContext, useRef, useState } from 'react';
var ModalType;
(function (ModalType) {
    ModalType[ModalType["Alert"] = 0] = "Alert";
    ModalType[ModalType["Confirm"] = 1] = "Confirm";
    ModalType[ModalType["Prompt"] = 2] = "Prompt";
})(ModalType || (ModalType = {}));
const toasters = {
    bottom: createToaster({ placement: 'bottom', duration: 2000 }),
    'top-right': createToaster({ placement: 'top-end', duration: 2000 })
};
const toast = ({ status, isClosable, position, ...rest }) => {
    toasters[position ?? 'bottom'].create({
        ...rest,
        type: status ?? 'info',
        closable: isClosable ?? true
    });
};
const defaultContext = {
    alert() {
        throw new Error('<NotificationsProvider> is missing');
    },
    confirm() {
        throw new Error('<NotificationsProvider> is missing');
    },
    prompt() {
        throw new Error('<NotificationsProvider> is missing');
    },
    toast() {
        throw new Error('<NotificationsProvider> is missing');
    }
};
const Context = createContext(defaultContext);
export const NotificationsProvider = ({ children }) => {
    const [modal, setModal] = useState(null);
    const input = useRef(null);
    const ok = useRef(null);
    const createOpener = useCallback((type) => async (...props) => await new Promise(resolve => {
        const [args, defaultValue] = props;
        const handleClose = (e) => {
            e?.preventDefault();
            setModal(null);
            resolve(null);
        };
        const handleCancel = (e) => {
            e?.preventDefault();
            setModal(null);
            if (type === ModalType.Prompt)
                resolve(null);
            else
                resolve(false);
        };
        const handleOK = (e) => {
            e?.preventDefault();
            setModal(null);
            if (type === ModalType.Prompt)
                resolve(input.current?.value);
            else
                resolve(true);
        };
        const Content = () => {
            const [inputValue, setInputValue] = useState(defaultValue || '');
            return (_jsxs(_Fragment, { children: [_jsx(Dialog.Header, { children: _jsxs(Stack, { direction: "row", alignItems: "center", children: [args.icon && (_jsx(Icon, { bg: "brand.100", color: "brand.500", borderRadius: "full", boxSize: "10", p: "2", asChild: true, children: _jsx(args.icon, {}) })), _jsx(Text, { children: args.title })] }) }), _jsx(Dialog.Body, { mt: "0", mb: 2, children: _jsxs(Stack, { gap: 5, children: [_jsx(Text, { children: args.message }), type === ModalType.Prompt && (_jsx(_Fragment, { children: args.options ? (_jsxs(NativeSelect.Root, { children: [_jsx(NativeSelect.Field, { ref: input, placeholder: args.placeholder, defaultValue: defaultValue, onChange: e => setInputValue(e.target.value), value: inputValue, children: args.options.map(option => (_jsx("option", { value: option.id, children: option.label }, option.id))) }), _jsx(NativeSelect.Indicator, {})] })) : (_jsx(Input, { ref: input, placeholder: args.placeholder, defaultValue: defaultValue, onChange: e => setInputValue(e.target.value), value: inputValue })) }))] }) }), _jsxs(Dialog.Footer, { bg: "bg.subtle", py: "3", children: [type !== ModalType.Alert && (_jsx(Button, { mr: 3, variant: "outline", onClick: handleCancel, children: args.cancelText || 'Cancel' })), _jsx(Button, { onClick: handleOK, ref: ok, disabled: type === ModalType.Prompt && inputValue === '', children: args.confirmText || 'OK' })] })] }));
        };
        setModal(_jsx(Dialog.Root, { open: true, initialFocusEl: () => (type === ModalType.Prompt ? input : ok).current, onOpenChange: (e) => {
                if (!e.open) {
                    handleClose();
                }
            }, children: _jsxs(Portal, { children: [_jsx(Dialog.Backdrop, {}), _jsx(Dialog.Positioner, { children: _jsx(Dialog.Content, { overflow: "hidden", children: _jsx(Content, {}) }) })] }) }));
    }), [children]);
    return (_jsxs(Context.Provider, { value: {
            alert: createOpener(ModalType.Alert),
            confirm: createOpener(ModalType.Confirm),
            prompt: createOpener(ModalType.Prompt),
            toast
        }, children: [_jsx(Portal, { children: Object.entries(toasters).map(([position, toaster]) => (_jsx(Toaster, { toaster: toaster, children: ({ title, description, closable }) => (_jsxs(Toast.Root, { width: { md: 'sm' }, children: [_jsx(Toast.Indicator, {}), _jsxs(Stack, { gap: "1", flex: "1", maxWidth: "100%", children: [title && _jsx(Toast.Title, { children: title }), description && (_jsx(Toast.Description, { children: description }))] }), closable && _jsx(Toast.CloseTrigger, {})] })) }, position))) }), modal, children] }));
};
export const useNotificationsContext = () => useContext(Context);
